import { ReceiptText } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'

import { type InvoiceRow, useInvoicesApi } from 'src/api/db/apiInvoice'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { EmptyState } from 'src/shared/components/EmptyState'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { PageContainer } from 'src/shared/components/PageContainer'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useToast } from 'src/shared/hooks/use-toast'

import { DeleteInvoiceDialog } from './components/DeleteInvoiceDialog'
import { InvoiceList } from './components/InvoiceList'
import { InvoiceUploadButton } from './components/InvoiceUploadButton'

type InvoicesPageProps = {
  embedded?: boolean
}

export const InvoicesPage = (props: InvoicesPageProps = {}) => {
  const { embedded = false } = props
  const { t } = useLocalization()
  const { toast } = useToast()
  const isAdmin = useIsAdmin()
  const org_id = useGetOrgId()

  const { invoices, isLoadingInvoices, uploadInvoice, deleteInvoice } =
    useInvoicesApi({ org_id })

  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceRow | null>(
    null
  )

  const shell = (children: ReactNode) =>
    embedded ? (
      <Column className="gap-4">{children}</Column>
    ) : (
      <PageContainer className="gap-4">{children}</PageContainer>
    )

  const handleUpload = async (file: File) => {
    if (!isAdmin) return
    try {
      await uploadInvoice.mutateAsync(file)

      toast({
        title: t('Invoice uploaded', 'Faktura uppladdad'),
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: t('Upload failed', 'Uppladdning misslyckades'),
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete || !isAdmin) return

    try {
      await deleteInvoice.mutateAsync(invoiceToDelete)
      setInvoiceToDelete(null)

      toast({
        title: t('Invoice deleted', 'Faktura borttagen'),
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: t('Delete failed', 'Borttagning misslyckades'),
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  return shell(
    <>
      <Column className="gap-4">
        {!embedded && (
          <Row className="items-center gap-2">
            <ReceiptText
              size={20}
              strokeWidth={2}
              className="shrink-0"
              aria-hidden
            />
            <h1>{t('Invoices', 'Fakturor')}</h1>
          </Row>
        )}

        <p className="text-sm md:text-base text-gray-600">
          {isAdmin
            ? t(
                'Upload invoice files for your organization. You can view or delete them at any time.',
                'Ladda upp fakturafiler för din organisation. Du kan visa eller ta bort dem när som helst.'
              )
            : t(
                'View invoices for your organization.',
                'Visa fakturor för din organisation.'
              )}
        </p>

        {!org_id && (
          <EmptyState
            icon={
              <ReceiptText size={36} strokeWidth={2} className="shrink-0" />
            }
            title={t('Select an organization', 'Välj en organisation')}
            description={t(
              isAdmin
                ? 'Select an organization to manage invoices.'
                : 'Select an organization to view invoices.',
              isAdmin
                ? 'Välj en organisation för att hantera fakturor.'
                : 'Välj en organisation för att visa fakturor.'
            )}
          />
        )}

        {org_id && isLoadingInvoices && <FullScreenLoader />}

        {org_id && !isLoadingInvoices && (
          <Column className="gap-4">
            {isAdmin && (
              <InvoiceUploadButton
                disabled={!org_id}
                isUploading={uploadInvoice.isPending}
                onPickFile={handleUpload}
              />
            )}

            {invoices.length === 0 ? (
              <EmptyState
                icon={
                  <ReceiptText size={36} strokeWidth={2} className="shrink-0" />
                }
                title={t('No invoices yet', 'Inga fakturor ännu')}
                description={t(
                  isAdmin
                    ? 'Upload a PDF or image to add your first invoice.'
                    : 'No invoices have been uploaded for this organization yet.',
                  isAdmin
                    ? 'Ladda upp en PDF eller en bild för att lägga till din första faktura.'
                    : 'Inga fakturor har laddats upp för den här organisationen ännu.'
                )}
              />
            ) : (
              <InvoiceList
                invoices={invoices}
                canDelete={isAdmin}
                onRequestDelete={(row) => setInvoiceToDelete(row)}
              />
            )}
          </Column>
        )}
      </Column>

      <DeleteInvoiceDialog
        open={isAdmin && invoiceToDelete != null}
        fileName={invoiceToDelete?.file_name ?? ''}
        onOpenChange={(open) => {
          if (!open) setInvoiceToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
      />
    </>
  )
}
